import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * PROA (Planejamento Orcamentario Anual) State Management
 *
 * Manages:
 * - Selected scenario for comparison views
 * - Year selection
 * - UI preferences for PROA views
 */

interface ProaScenario {
  scenarioId: string
  name: string
  year: number
  isOfficial: boolean
  isLocked: boolean
}

interface ProaState {
  // Selected scenario
  selectedScenarioId: string | null
  selectedScenario: ProaScenario | null
  selectedYear: number

  // UI preferences
  showProaInDRE: boolean
  comparisonMode: 'variance' | 'percentage' | 'both'

  // Actions
  setSelectedScenario: (scenario: ProaScenario | null) => void
  setSelectedScenarioId: (id: string | null) => void
  setSelectedYear: (year: number) => void
  setShowProaInDRE: (show: boolean) => void
  setComparisonMode: (mode: 'variance' | 'percentage' | 'both') => void
  reset: () => void
}

const initialState = {
  selectedScenarioId: null,
  selectedScenario: null,
  selectedYear: new Date().getFullYear(),
  showProaInDRE: true,
  comparisonMode: 'both' as const,
}

/**
 * PROA Store
 *
 * Persists:
 * - Selected scenario ID
 * - Selected year
 * - UI preferences (showProaInDRE, comparisonMode)
 *
 * Does not persist:
 * - Full scenario object (fetched from API)
 */
export const useProaStore = create<ProaState>()(
  persist(
    (set) => ({
      ...initialState,

      setSelectedScenario: (scenario) =>
        set({
          selectedScenario: scenario,
          selectedScenarioId: scenario?.scenarioId ?? null,
        }),

      setSelectedScenarioId: (id) =>
        set({
          selectedScenarioId: id,
          // Clear scenario object - will be refetched
          selectedScenario: null,
        }),

      setSelectedYear: (year) =>
        set({
          selectedYear: year,
          // Reset scenario when year changes
          selectedScenarioId: null,
          selectedScenario: null,
        }),

      setShowProaInDRE: (show) =>
        set({ showProaInDRE: show }),

      setComparisonMode: (mode) =>
        set({ comparisonMode: mode }),

      reset: () => set(initialState),
    }),
    {
      name: 'proa-state',
      // Only persist IDs and preferences, not full scenario data
      partialize: (state) => ({
        selectedScenarioId: state.selectedScenarioId,
        selectedYear: state.selectedYear,
        showProaInDRE: state.showProaInDRE,
        comparisonMode: state.comparisonMode,
      }),
    }
  )
)

/**
 * Selector for checking if PROA is enabled for current view
 */
export const useIsProaEnabled = () => {
  return useProaStore((state) => state.showProaInDRE && state.selectedScenarioId !== null)
}

/**
 * Selector for getting the current scenario context
 */
export const useProaContext = () => {
  return useProaStore((state) => ({
    scenarioId: state.selectedScenarioId,
    scenario: state.selectedScenario,
    year: state.selectedYear,
    comparisonMode: state.comparisonMode,
  }))
}
